import {
  type HttpValidationError,
  type QuoteDetailPublic,
  type QuotePatchResult,
  QuotesService,
  QuoteTasksService,
  type TaskOperation,
  type UpdateQuote,
} from "@common/client"
import {
  type QueryKey,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import type { AxiosError } from "axios"
import {
  makeAxiosMutation,
  makeGroupPages,
  makeInfiniteQuery,
  makeQuery,
  unwrap,
} from "../utils"
import { quoteActivityBaseKey, quoteActivityKey } from "./activity"
import { quoteCommentsKey } from "./comment"
import { isPredictableUpdate, quoteWithUpdate } from "./optimistic-quote"
import { payerAllocationsKey } from "./payer-allocation"
import {
  avrefPricesBaseKey,
  cannedTasksBaseKey,
  labourBaseKey,
  pastPartsBaseKey,
  priceHistoryBaseKey,
  taskPackagesBaseKey,
} from "./quote_task"
import { quoteRevisionsBaseKey, quoteRevisionsKey } from "./revision"

export const quotesBaseKey = ["quotes"] as const

export const quotesListKey = (
  params: Parameters<typeof QuotesService.listQuotes>[0],
) => [...quotesBaseKey, params] as const

export const quotesQuery = makeQuery({
  serviceCall: QuotesService.listQuotes,
  queryKey: quotesListKey,
})

export const quoteFilterOptionsQuery = makeQuery({
  serviceCall: QuotesService.filterOptions,
  queryKey: (params) => [...quotesBaseKey, "filter-options", params],
})

export type QuoteOverviewParams = Parameters<
  typeof QuotesService.getOverview
>[0]

export const quoteOverviewQuery = makeQuery({
  serviceCall: QuotesService.getOverview,
  queryKey: (params: QuoteOverviewParams) => [
    ...quotesBaseKey,
    "overview",
    params,
  ],
})

const QUOTES_PAGE_SIZE = 50

export type QuotePagesParams = Omit<
  Parameters<typeof QuotesService.listQuotes>[0],
  "skip" | "limit"
>

export const quotesInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: QuotePagesParams, page) =>
    QuotesService.listQuotes({ ...params, ...page }),
  queryKey: (params) => [...quotesBaseKey, "infinite", params],
  pageSize: QUOTES_PAGE_SIZE,
})

export const quoteGroupPages = makeGroupPages({
  serviceCall: (params: QuotePagesParams, page) =>
    QuotesService.listQuotes({ ...params, ...page }),
  queryKey: (params) => [...quotesBaseKey, "grouped", params],
  pageSize: QUOTES_PAGE_SIZE,
})

export const quoteBaseKey = ["quote"] as const

export const quoteKey = (id: string) => [...quoteBaseKey, id] as const

export const quoteQuery = makeQuery({
  serviceCall: QuotesService.get,
  queryKey: (params) => quoteKey(params.quote_id),
})

export const partHistoryKey = (id: string) =>
  ["quote-part-history", id] as const

export const partHistoryQuery = makeQuery({
  serviceCall: QuotesService.listPartHistory,
  queryKey: (params) => partHistoryKey(params.quote_id),
})

export const quoteTaxKey = (id: string) => ["quote-tax", id] as const

export const quoteTaxQuery = makeQuery({
  serviceCall: QuotesService.getTax,
  queryKey: (params) => quoteTaxKey(params.quote_id),
})

export const quoteRateCardsKey = (id: string) =>
  ["quote-rate-cards", id] as const

export const quoteRateCardsQuery = makeQuery({
  serviceCall: QuotesService.listRateCards,
  queryKey: (params) => quoteRateCardsKey(params.quote_id),
})

export const quoteReviewHistoryBaseKey = ["quote-review-history"] as const

export const quoteReviewHistoryKey = (id: string) =>
  [...quoteReviewHistoryBaseKey, id] as const

export const quoteReviewHistoryQuery = makeQuery({
  serviceCall: QuotesService.reviewHistory,
  queryKey: (params) => quoteReviewHistoryKey(params.quote_id),
})

export function quoteRefetchKeys(id?: string): readonly QueryKey[] {
  return id
    ? [
        quotesBaseKey,
        quoteKey(id),
        quoteRevisionsKey(id),
        quoteActivityKey(id),
        quoteTaxKey(id),
        quoteCommentsKey(id),
        payerAllocationsKey(id),
        quoteReviewHistoryKey(id),
      ]
    : [
        quotesBaseKey,
        quoteBaseKey,
        quoteRevisionsBaseKey,
        quoteActivityBaseKey,
        quoteReviewHistoryBaseKey,
      ]
}

export const useCreateQuoteMutation = makeAxiosMutation({
  serviceCall: QuotesService.create,
  invalidates: () => [quotesBaseKey],
})

function setsDescription(operation: TaskOperation): boolean {
  switch (operation.operation) {
    case "create":
    case "update":
      return operation.description !== undefined
    case "create_from_template":
      return true
    case "apply_template":
    case "remove":
      return false
  }
}

function setsCharges(operation: TaskOperation): boolean {
  switch (operation.operation) {
    case "create":
    case "update":
      return (operation.charges ?? []).length > 0
    case "create_from_template":
    case "apply_template":
      return true
    case "remove":
      return false
  }
}

function setsPart(operation: TaskOperation): boolean {
  switch (operation.operation) {
    case "create":
    case "update":
      return operation.part_id !== undefined
    case "create_from_template":
    case "apply_template":
    case "remove":
      return false
  }
}

function anyTask(
  update: UpdateQuote,
  check: (operation: TaskOperation) => boolean,
): boolean {
  return (update.tasks ?? []).some(check)
}

const descriptionKeys = [labourBaseKey, cannedTasksBaseKey, pastPartsBaseKey]

const updateQuoteMutationKey = ["update-quote"] as const

const updateQuoteScope = { id: "update-quote" }

type UpdateQuoteInput = Parameters<typeof QuotesService.applyUpdate>[0]

function quoteUpdateRelatedKeys(params: UpdateQuoteInput): QueryKey[] {
  return [
    quotesBaseKey,
    quoteRevisionsKey(params.quote_id),
    quoteActivityKey(params.quote_id),
    quoteTaxKey(params.quote_id),
    quoteRateCardsKey(params.quote_id),
    ...(anyTask(params.updateQuote, setsDescription) ? descriptionKeys : []),
    ...(anyTask(params.updateQuote, setsCharges)
      ? [priceHistoryBaseKey, avrefPricesBaseKey]
      : []),
    ...(anyTask(params.updateQuote, setsPart) ? [taskPackagesBaseKey] : []),
  ]
}

export const useUpdateQuoteMutation = makeAxiosMutation({
  serviceCall: QuotesService.applyUpdate,
  mutationKey: updateQuoteMutationKey,
  scope: updateQuoteScope,
  invalidates: (params) => [
    quoteKey(params.quote_id),
    ...quoteUpdateRelatedKeys(params),
  ],
})

export const useClassifyTaskGroupsMutation = makeAxiosMutation({
  serviceCall: QuoteTasksService.classifyTaskGroups,
  invalidates: () => [quoteBaseKey, quoteActivityBaseKey],
})

function describedTaskIds(
  body: UpdateQuote,
  result: QuotePatchResult,
): string[] {
  const tasks = body.tasks ?? []
  const created = tasks.filter((task) => task.operation === "create")
  return [
    ...result.created_task_ids.filter(
      (_, index) => !created[index]?.task_group_id,
    ),
    ...tasks.flatMap((task) =>
      task.operation === "update" &&
      task.description !== undefined &&
      task.task_group_id === undefined
        ? [task.id]
        : [],
    ),
  ]
}

export const useOptimisticQuoteUpdateMutation = (
  quoteId: string,
  {
    onError,
  }: {
    onError: (error: AxiosError<HttpValidationError>) => void
  },
) => {
  const queryClient = useQueryClient()
  const classify = useClassifyTaskGroupsMutation()
  const mutationKey = [...updateQuoteMutationKey, quoteId]
  const othersSaving = () => queryClient.isMutating({ mutationKey }) > 1

  return useMutation<
    QuotePatchResult,
    AxiosError<HttpValidationError>,
    UpdateQuoteInput
  >({
    mutationKey,
    scope: updateQuoteScope,
    mutationFn: (input) => QuotesService.applyUpdate(input).then(unwrap),
    onMutate: async (input) => {
      if (!isPredictableUpdate(input.updateQuote)) return
      const cached = { queryKey: quoteKey(input.quote_id), exact: true }
      if (!othersSaving()) {
        await queryClient.cancelQueries(cached)
      } else if (queryClient.isFetching(cached) > 0) {
        await queryClient.refetchQueries(cached, { cancelRefetch: false })
      }
      queryClient.setQueryData<QuoteDetailPublic>(
        cached.queryKey,
        (quote) => quote && quoteWithUpdate(quote, input.updateQuote),
      )
    },
    onSuccess: (result, input) => {
      const taskIds = describedTaskIds(input.updateQuote, result)
      if (taskIds.length > 0) {
        classify.mutate({ classifyTasks: { task_ids: taskIds } })
      }
    },
    onError,
    onSettled: (_result, _error, input) => {
      const predicted = isPredictableUpdate(input.updateQuote)
      const refetchQuote = !predicted || !othersSaving()
      const settled = Promise.all(
        [
          ...(refetchQuote ? [quoteKey(input.quote_id)] : []),
          ...quoteUpdateRelatedKeys(input),
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      )
      return predicted ? undefined : settled
    },
  })
}

export const useChangeDispositionMutation = makeAxiosMutation({
  serviceCall: QuotesService.changeDisposition,
  scope: updateQuoteScope,
  invalidates: (params) => [
    quotesBaseKey,
    quoteKey(params.quote_id),
    quoteRevisionsKey(params.quote_id),
    quoteActivityKey(params.quote_id),
    quoteReviewHistoryKey(params.quote_id),
  ],
})

export const useResendEmailMutation = makeAxiosMutation({
  serviceCall: QuotesService.resendEmail,
  scope: updateQuoteScope,
  invalidates: (params) => [quotesBaseKey, quoteKey(params.quote_id)],
})

export const useSetReviewersMutation = makeAxiosMutation({
  serviceCall: QuotesService.setReviewers,
  scope: updateQuoteScope,
  invalidates: (params) => [quotesBaseKey, quoteKey(params.quote_id)],
})

export const useReviewTasksMutation = makeAxiosMutation({
  serviceCall: QuotesService.reviewTasks,
  scope: updateQuoteScope,
  invalidates: (params) => [
    quotesBaseKey,
    quoteKey(params.quote_id),
    quoteCommentsKey(params.quote_id),
    quoteActivityKey(params.quote_id),
    quoteReviewHistoryKey(params.quote_id),
  ],
})

export const useUndoTaskReviewsMutation = makeAxiosMutation({
  serviceCall: QuotesService.undoTaskReviews,
  scope: updateQuoteScope,
  invalidates: (params) => [
    quotesBaseKey,
    quoteKey(params.quote_id),
    quoteActivityKey(params.quote_id),
    quoteReviewHistoryKey(params.quote_id),
  ],
})

export const customerPreviewKey = (id: string) =>
  ["quote-customer-preview", id] as const

export const customerPreviewQuery = makeQuery({
  serviceCall: QuotesService.previewForCustomer,
  queryKey: (params) => customerPreviewKey(params.quote_id),
})

export const useDuplicateQuoteMutation = makeAxiosMutation({
  serviceCall: QuotesService.duplicate,
  invalidates: () => [quotesBaseKey],
})

export const useArchiveQuoteMutation = makeAxiosMutation({
  serviceCall: QuotesService.archive,
  invalidates: (params) => [
    quotesBaseKey,
    quoteKey(params.quote_id),
    quoteRevisionsKey(params.quote_id),
  ],
})

export const useRestoreQuoteMutation = makeAxiosMutation({
  serviceCall: QuotesService.restore,
  invalidates: (params) => [
    quotesBaseKey,
    quoteKey(params.quote_id),
    quoteRevisionsKey(params.quote_id),
  ],
})

export const useDeleteQuoteMutation = makeAxiosMutation({
  serviceCall: QuotesService.delete,
  invalidates: (params) => [
    quotesBaseKey,
    quoteKey(params.quote_id),
    quoteRevisionsKey(params.quote_id),
  ],
})

export const useCreateQuoteWorkPackageMutation = makeAxiosMutation({
  serviceCall: QuotesService.createWorkPackage,
  scope: updateQuoteScope,
  invalidates: (params) => [
    quotesBaseKey,
    quoteKey(params.quote_id),
    quoteRevisionsKey(params.quote_id),
  ],
})

export const useExportQuoteMutation = makeAxiosMutation({
  serviceCall: QuotesService.exportPdf,
  invalidates: (params) => [quoteActivityKey(params.quote_id)],
})

export const useMergeQuoteTasksMutation = makeAxiosMutation({
  serviceCall: QuoteTasksService.mergeTasks,
  invalidates: (_params, merged) => [
    quotesBaseKey,
    quoteKey(merged.quote_id),
    quoteRevisionsKey(merged.quote_id),
    quoteActivityKey(merged.quote_id),
    labourBaseKey,
    priceHistoryBaseKey,
  ],
})

export const useSendQuoteToSalesforceMutation = makeAxiosMutation({
  serviceCall: QuotesService.sendQuoteToSalesforce,
  invalidates: (params) => [quotesBaseKey, quoteKey(params.quote_id)],
})
